import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { isAfter, isBefore } from 'date-fns';

import { ResultListModel } from '@common-lib';

import { VoucherListModel, voucherClient } from '@portal-clients/voucher';

import { useClientQuery, useInfiniteScrollClientQuery } from '@frontend/hooks';
import { useLicense } from '@frontend/licenses';
import {
  KeyValueStore,
  useOptionalPortalConfiguration,
  useOptionalUser,
} from '@frontend/utils';

const fetchVouchers = (pageToken: string | undefined) => {
  return voucherClient.list({
    pageSize: 20,
    pageToken,
    sortBy: {
      field: 'CREATED_AT',
      order: 'DESC',
    },
    filter: {
      ids: undefined,
      operatorIds: undefined,
      voucherIssueIds: undefined,
    },
  });
};

export type UseVoucherStatus = 'active' | 'used';

export interface UseVouchersProps {
  onFinally?(): void;
}

export interface UseCountVoucherProps {
  onError?(): void;
}

export const getVouchersQueryKey = () => ['vouchers'];
export const getCountVouchersQueryKey = () => ['countVouchers'];
export const getUsedVouchersQueryKey = (): QueryKey => ['usedVouchers'];

const filterActiveVouchers = (voucher: VoucherListModel, now: Date) => {
  if (voucher.revoked) {
    return false;
  }

  if (!voucher.validity?.to && voucher.claims < voucher.maxClaims) {
    return true;
  }

  if (voucher.validity?.to && voucher.claims < voucher.maxClaims) {
    const expiry = new Date(voucher.validity.to);
    return expiry >= now;
  }

  return false;
};

export const useVoucherQuery = ({ onFinally }: UseVouchersProps) => {
  return useInfiniteScrollClientQuery({
    enabled: true,
    queryKey: getVouchersQueryKey(),
    onFetch: async (pageToken) => {
      const response = await fetchVouchers(pageToken);

      // We need to filter out active and valid vouchers.
      // In the future this filtering should be handled by the API.
      const now = new Date();
      const filtered: ResultListModel<VoucherListModel> = {
        ...response,
        results: response.results.filter((v) => {
          return filterActiveVouchers(v, now);
        }),
      };

      return filtered;
    },
    onFinally,
  });
};

export const useCountVoucherQuery = ({ onError }: UseCountVoucherProps) => {
  const config = useOptionalPortalConfiguration();
  const hasLicense = useLicense('vouchers');
  const { user } = useOptionalUser();

  return useClientQuery({
    enabled: !!user && config?.features?.voucher?.enabled && hasLicense,
    queryKey: getCountVouchersQueryKey(),
    onFetch: async () => {
      const response = await fetchVouchers(undefined);

      const lastVisitedBenefits = await KeyValueStore.get(
        `portal_benefits_datetime_${config!.portalId}`,
      );

      const lastVisitedBenefitsDate = lastVisitedBenefits
        ? new Date(lastVisitedBenefits)
        : undefined;

      // We need to filter out active and valid vouchers.
      // In the future this filtering should be handled by the API.
      const now = new Date();
      const filtered: ResultListModel<VoucherListModel> = {
        ...response,
        results: response.results
          .filter((v) => {
            return filterActiveVouchers(v, now);
          })
          .filter((voucher) => {
            if (!lastVisitedBenefitsDate) {
              return true;
            }

            if (
              voucher.validity?.from &&
              isAfter(voucher.validity?.from, lastVisitedBenefitsDate)
            ) {
              return true;
            }

            if (
              (voucher.validity?.from === undefined ||
                isBefore(voucher.validity?.from, Date.now())) &&
              isAfter(voucher.issuedAt, lastVisitedBenefitsDate)
            ) {
              return true;
            }

            return false;
          }),
      };

      return { count: filtered.results.length };
    },
    onError,
  });
};

export const useUsedVoucherQuery = ({ onFinally }: UseVouchersProps) => {
  return useInfiniteScrollClientQuery({
    enabled: true,
    queryKey: getUsedVouchersQueryKey(),
    onFetch: async (pageToken) => {
      const response = await fetchVouchers(pageToken);

      // We need to filter out revoked and expired vouchers.
      // In the future this filtering should be handled by the API.
      const now = new Date();
      const filtered: ResultListModel<VoucherListModel> = {
        ...response,
        results: response.results.filter((v) => {
          if (v.revoked) {
            return false;
          }

          if (v.claims === v.maxClaims) {
            return true;
          }

          if (v.validity?.to) {
            const expiry = new Date(v.validity.to);

            if (expiry < now && v.claims > 0) {
              return true;
            }
          }

          return false;
        }),
      };

      return filtered;
    },
    onFinally,
  });
};

export const usePrefetchAllVouchers = () => {
  const queryClient = useQueryClient();
  const config = useOptionalPortalConfiguration();
  const hasLicense = useLicense('vouchers');
  const { user } = useOptionalUser();

  if (!hasLicense || !config?.features?.voucher?.enabled || !user) {
    return;
  }

  queryClient.prefetchInfiniteQuery({
    pages: 1,
    queryKey: getVouchersQueryKey(),
    initialPageParam: undefined,
    getNextPageParam: (lastPage: ResultListModel<VoucherListModel>) => {
      return lastPage.nextPageToken;
    },
    queryFn: async ({ pageParam: pageToken }) => {
      const response = await fetchVouchers(pageToken);

      // We need to filter out active and valid vouchers.
      // In the future this filtering should be handled by the API.
      const now = new Date();
      const filtered: ResultListModel<VoucherListModel> = {
        ...response,
        results: response.results.filter((v) => {
          return filterActiveVouchers(v, now);
        }),
      };

      return filtered;
    },
  });
};
